import useOrganizations from "contexts/Organizations/useOrganizations";
import useProjects from "contexts/Projects/useProjects";
import useIsB2cContext from "./useIsB2cContext";

/**
 * Whether this user is a consumer whose organization holds exactly one project —
 * i.e. whether the projects gallery is a dead end for them.
 *
 * Everything that leads there is hidden on this: the project switcher and the
 * header logo's destination (components/Header/Header.tsx), and the back arrow
 * on the project page (views/Projects/views/Project). A one-card gallery they
 * cannot add to is a screen with nothing on it for them.
 *
 * Two shapes qualify:
 *  - a personal organization, which holds the single project created with it;
 *  - a BMS endcustomer, whose one project sits in the shared BMS org instead, so
 *    the organization says nothing and the project count has to decide.
 *
 * Counted rather than assumed: the b2c flow gives an endcustomer exactly one
 * project, but should they ever end up with a second, every one of those
 * controls has to come back.
 *
 * Consumers only. A business organization down to its last project still needs
 * the way to its gallery — that is where the next project gets created.
 *
 * `totalResults`, not `projects.length`: the list is paginated, so length only
 * counts the pages loaded so far. Undefined while the count is still unknown,
 * which reads as "not a dead end" — the same fail-open choice as canMoveScenes
 * in Project.tsx, so a failed fetch cannot strand someone with no way back.
 */
const useIsSingleProjectConsumer = (): boolean => {
	const { current } = useOrganizations();
	const { pagedResponse } = useProjects();
	const isB2cContext = useIsB2cContext();

	return Boolean(
		current?.isPersonal ||
			(isB2cContext && pagedResponse?.totalResults === 1),
	);
};

export default useIsSingleProjectConsumer;
